import { Helmet } from 'react-helmet-async';

import { SiteFooter, SiteNav } from '../../components/SiteChrome';

const STATS = [
  { big: '3', label: 'fields to log an interaction — name, number, note' },
  { big: '10s', label: 'to capture a call before you forget it' },
  { big: '0', label: 'dashboards, pipelines, or setup to configure' },
];

const STEPS = [
  {
    n: '01',
    title: 'Open to a blank note',
    body: 'ClientBridge launches straight into a ready-to-type form with today’s date already set. No menus, no “new record”.',
  },
  {
    n: '02',
    title: 'Jot the interaction',
    body: 'Client name, phone, and what was said. Save it in seconds — during the call or right after.',
  },
  {
    n: '03',
    title: 'Watch the log build',
    body: 'Every entry drops into a clean timeline grouped by day. Your whole business history, scroll-back ready.',
  },
];

const NO_FLUFF = [
  {
    title: 'No pipeline stages',
    body: 'You’re logging what happened, not dragging cards across a board.',
  },
  {
    title: 'No required fields',
    body: 'Type as much or as little as you want. A name alone is a valid entry.',
  },
  {
    title: 'No onboarding maze',
    body: 'Sign in with Apple or Google, pick your industry, done.',
  },
  {
    title: 'No reports to build',
    body: 'The timeline is the report. Grouped by day, always current.',
  },
  {
    title: 'No per-seat pricing traps',
    body: 'One simple app for the way you actually work.',
  },
  {
    title: 'No learning curve',
    body: 'If you can use a notes app, you already know ClientBridge.',
  },
];

const todayLabel = new Date().toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
});

const Check = () => (
  <span className="mt-0.5 flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-orange">
    <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M7 12.5l3.2 3.2L17.5 8"
        fill="none"
        stroke="#FDFCF8"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

export function Landing() {
  return (
    <>
      <Helmet>
        <title>ClientBridge — the simplest CRM ever made</title>
        <meta
          name="description"
          content="Log the day. Run the business. No pipelines, no dashboards, no fluff — just one clean log of every client interaction."
        />
      </Helmet>

      <section className="w-full overflow-hidden">
        <SiteNav />

        {/* ===== HERO ===== */}
        <div className="mx-auto mt-2 max-w-[1120px] rounded-[34px] bg-periwinkle px-6 pt-16 md:px-10 md:pt-[82px]">
          <div className="mx-auto max-w-[760px] text-center">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-cream-light/80">
              The simplest CRM ever made
            </span>
            <h1 className="mt-5 font-display text-[44px] font-extrabold leading-[0.96] tracking-[-0.03em] text-cream-light md:text-[74px]">
              Log the day.
              <br />
              Run the business.
            </h1>
            <p className="mx-auto mt-5 max-w-[520px] font-body text-base leading-[1.55] text-cream-light/85 md:text-lg">
              No pipelines. No dashboards. No fluff. Just add your interactions
              every day and keep one clean log of everything that runs your
              business.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href="#get-started"
                className="rounded-[14px] bg-orange px-7 py-4 font-body text-base font-semibold text-cream-light hover:bg-orange-dark"
              >
                Start logging free
              </a>
              <a
                href="#how"
                className="rounded-[14px] bg-cream-light/[0.16] px-7 py-4 font-body text-base font-semibold text-cream-light hover:bg-cream-light/25"
              >
                See it in action
              </a>
            </div>
          </div>

          {/* app peek — the real capture form, cut off by the fold */}
          <div className="mx-auto mt-14 max-w-[400px] rounded-t-[22px] bg-cream px-[22px] pt-[22px] shadow-[0_-2px_0_rgba(36,56,44,.05),0_30px_60px_-30px_rgba(36,56,44,.5)]">
            <div className="mb-3.5 flex items-center justify-between">
              <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-warm-faint">
                New interaction
              </span>
              <span className="rounded-full bg-orange-tint px-2.5 py-1.5 font-mono text-[10px] font-medium text-orange">
                Today · {todayLabel}
              </span>
            </div>
            <div className="font-body text-[21px] font-semibold leading-tight text-forest-deep">
              Sofia Rangel
            </div>
            <div className="my-3 h-px bg-forest/10" />
            <div className="font-mono text-[13px] text-forest-slate">
              +1 415 555 0138
            </div>
            <div className="my-3 h-px bg-forest/10" />
            <div className="pb-5 font-body text-sm leading-[1.5] text-forest-soft">
              Confirmed the Thursday walkthrough — bringing the revised floor
              plan.
            </div>
          </div>
        </div>

        {/* ===== STAT STRIP ===== */}
        <div className="mx-auto flex max-w-[1120px] flex-wrap justify-center gap-5 px-6 py-16 text-center md:px-10">
          {STATS.map((s) => (
            <div key={s.big} className="min-w-[220px] flex-1">
              <div className="font-display text-[46px] font-extrabold leading-none tracking-[-0.02em] text-orange">
                {s.big}
              </div>
              <div className="mt-2 font-body text-[14.5px] leading-[1.45] text-forest-muted">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* ===== HOW IT WORKS ===== */}
        <div id="how" className="mx-auto max-w-[1120px] px-6 pb-5 pt-9 md:px-10">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-warm-faint">
            How it works
          </span>
          <h2 className="mt-3.5 max-w-[600px] font-display text-[32px] font-bold leading-[1.02] tracking-[-0.02em] text-forest-deep md:text-[44px]">
            Three fields. One habit.
            <br />
            That’s the whole app.
          </h2>
        </div>
        <div className="mx-auto flex max-w-[1120px] flex-wrap gap-5 px-6 pb-5 pt-8 md:px-10">
          {STEPS.map((st) => (
            <div
              key={st.n}
              className="min-w-[280px] flex-1 rounded-[22px] border border-hairline bg-cream-light px-7 py-[30px]"
            >
              <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-forest font-mono text-[15px] font-bold text-cream-light">
                {st.n}
              </div>
              <h3 className="mt-5 font-display text-[22px] font-bold leading-[1.15] tracking-[-0.01em] text-forest-deep">
                {st.title}
              </h3>
              <p className="mt-2.5 font-body text-[15px] leading-[1.55] text-forest-muted">
                {st.body}
              </p>
            </div>
          ))}
        </div>

        {/* ===== WHY ===== */}
        <div
          id="why"
          className="mx-auto mt-11 max-w-[1120px] rounded-[34px] bg-forest px-6 py-16 md:px-10 md:py-[72px]"
        >
          <div className="mx-auto max-w-[640px] text-center">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-periwinkle">
              Why ClientBridge
            </span>
            <h2 className="mt-3.5 font-display text-[32px] font-bold leading-[1.04] tracking-[-0.02em] text-cream-light md:text-[44px]">
              Everything a CRM piles on,
              <br />
              we left out.
            </h2>
          </div>
          <div className="mt-11 flex flex-wrap gap-3.5">
            {NO_FLUFF.map((n) => (
              <div
                key={n.title}
                className="flex min-w-[300px] flex-1 items-start gap-3.5 rounded-2xl border border-cream-light/[0.09] bg-cream-light/[0.05] px-[22px] py-5"
              >
                <Check />
                <div>
                  <div className="font-body text-base font-semibold leading-[1.3] text-cream-light">
                    {n.title}
                  </div>
                  <div className="mt-1 font-body text-sm leading-[1.5] text-cream-light/[0.62]">
                    {n.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== CLOSING CTA ===== */}
        <div
          id="get-started"
          className="mx-auto max-w-[1120px] px-6 py-20 text-center md:px-10 md:py-[90px]"
        >
          <h2 className="font-display text-[38px] font-extrabold leading-none tracking-[-0.03em] text-forest-deep md:text-[58px]">
            Start today. Log tonight.
          </h2>
          <p className="mx-auto mt-4 max-w-[460px] font-body text-[17px] leading-[1.55] text-forest-muted">
            Your business is a series of conversations. Keep them all in one calm
            place.
          </p>
          <a
            href="#get-started"
            className="mt-7 inline-block rounded-[14px] bg-orange px-9 py-[17px] font-body text-base font-semibold text-cream-light hover:bg-orange-dark"
          >
            Get started free
          </a>
        </div>

        <SiteFooter />
      </section>
    </>
  );
}
